import type { ReleaseContext } from '../types/release.types';
import type { ObservabilityEnvironment } from '../types/tenant.types';

export function resolveEnvironment(raw?: string | null): ObservabilityEnvironment {
  const value = raw?.trim().toLowerCase() ?? '';
  if (!value) return 'development';
  if (value.includes('prod')) return 'production';
  if (value.includes('stag')) return 'staging';
  return 'development';
}

export type EnvironmentConfig = {
  enabled: boolean;
  service: string;
  environment: ObservabilityEnvironment;
  sampleRate: number;
  releaseId?: string;
  version?: string;
  gitCommitSha?: string;
  deploymentId?: string;
};

export function readEnvironmentConfig(
  env:
    | {
        OBSERVABILITY_ENABLED?: string;
        OBSERVABILITY_ENV?: string;
        OBSERVABILITY_SERVICE_NAME?: string;
        OBSERVABILITY_SAMPLE_RATE?: string;
        OBSERVABILITY_RELEASE_ID?: string;
        OBSERVABILITY_VERSION?: string;
        OBSERVABILITY_GIT_SHA?: string;
        OBSERVABILITY_DEPLOYMENT_ID?: string;
      }
    | undefined,
  defaults: Partial<EnvironmentConfig> = {},
): EnvironmentConfig {
  const rawEnabled = env?.OBSERVABILITY_ENABLED ?? '';
  const enabled = rawEnabled ? rawEnabled === 'true' : (defaults.enabled ?? true);
  const parsedRate = Number(env?.OBSERVABILITY_SAMPLE_RATE);
  const sampleRate =
    Number.isFinite(parsedRate) && parsedRate >= 0 && parsedRate <= 1
      ? parsedRate
      : (defaults.sampleRate ?? 1);

  return {
    enabled,
    service: env?.OBSERVABILITY_SERVICE_NAME ?? defaults.service ?? 'unknown-service',
    environment: resolveEnvironment(env?.OBSERVABILITY_ENV ?? defaults.environment),
    sampleRate,
    releaseId: env?.OBSERVABILITY_RELEASE_ID ?? defaults.releaseId,
    version: env?.OBSERVABILITY_VERSION ?? defaults.version,
    gitCommitSha: env?.OBSERVABILITY_GIT_SHA ?? defaults.gitCommitSha,
    deploymentId: env?.OBSERVABILITY_DEPLOYMENT_ID ?? defaults.deploymentId,
  };
}

export function toReleaseContext(config: EnvironmentConfig): ReleaseContext {
  return {
    releaseId: config.releaseId,
    version: config.version,
    gitCommitSha: config.gitCommitSha,
    deploymentId: config.deploymentId,
    environment: config.environment,
    service: config.service,
  };
}
